import React from 'react';
import { uniqBy } from 'lodash';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TranslateIcon from '@mui/icons-material/Translate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CurrentUser from 'auth/currentUser';
import GeneralConfig from 'data/GeneralConfig';
import GeneralConfigContext from 'data/GeneralConfigContext';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

interface Locale {
  code: string;
  text: string;
}

interface SetLocaleCode {
  (code: string): void;
}

interface Props {
  localeOptions: Locale[];
  defaultLocale: Locale;
  onSelect: SetLocaleCode;
}

function LocaleButtonWithContext(props: Props) {
  const { localeOptions, onSelect, defaultLocale } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [localeCode, setLocaleCode] = React.useState<string>(defaultLocale.code ?? 'en-GB');

  const handleSelect = (code: string) => {
    setLocaleCode(code);
    setAnchorEl(null); // close menu
    onSelect(code);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderMenuItem = (loca: Locale) => {
    const code = loca.code;
    const selected = code === localeCode;
    return (
      <MenuItem key={code} selected={selected} onClick={() => handleSelect(code)}>
        {loca.text}
      </MenuItem>
    );
  };

  return (
    <>
      <Button
        aria-controls="local-button"
        aria-haspopup="true"
        color="inherit"
        onClick={handleClick}
      >
        <TranslateIcon />
        <Typography style={{ minWidth: 50 }}>{defaultLocale?.text || 'English'}</Typography>
        <ExpandMoreIcon />
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {localeOptions.map((l) => renderMenuItem(l))}
      </Menu>
    </>
  );
}

export default function LocaleButton() {
  const { i18n } = useTranslation();
  const currentUser = CurrentUser.getInstance();
  const defaultLocaleCode = currentUser.getLocale();

  return (
    <div>
      <GeneralConfigContext.Consumer>
        {(localeConfig: GeneralConfig) => {
          const { setLocaleCode, resources } = localeConfig;
          // get all codes from the translation on cloud
          const cloudLocales = resources.map((r) => r.locale as Locale); // translation on cloud
          const allLocaleCode = uniqBy(cloudLocales, (l) => l.code);
          // default selected en-GB or first if not exist
          const currentLocale =
            allLocaleCode.find((l) => l.code === defaultLocaleCode) ?? allLocaleCode[0];

          const handleSelect = (code: string) => {
            i18n.changeLanguage(code); // change i18n setting
            currentUser.setLocale(code); // save code in localStorage
            setLocaleCode && setLocaleCode(code); // change the state of App.tsx to refresh the whole app
          };

          // Doesnot show language switch button if only one language returned
          if (allLocaleCode.length < 2) {
            return <></>;
          }
          return (
            <LocaleButtonWithContext
              localeOptions={allLocaleCode}
              defaultLocale={currentLocale}
              onSelect={handleSelect}
            />
          );
        }}
      </GeneralConfigContext.Consumer>
    </div>
  );
}
